<template>
<component
    :is='component'
    class='fw-button'
    :class='[
        component,
        sizeClass,
        colorClass,
        borderRadiusClass,
        {
            loading,
            ghost,
            active,
            "fw-button--inversed": inversed,
            "fw-button--padding__zero": paddingHorizontal,
            "fw-button--padding__right": paddingOnlyRight,
        },
    ]'
    :style='[{
        fontSize: fontSize,
        color: fontColor,
        minWidth,
    }, styles]'
    v-bind='$attrs'
    :tabindex='ghost ? -1 : 0'
    role='button'
    :aria-disabled='ghost'
    @click.capture='handleClick'
    @click.capture.native='handleClick'>
    <FwIcon v-if='iconLeft' class='h-mr-15' :icon='iconLeft' :color='iconColor'></FwIcon>
    <slot :style='{}'>
        {{ label }}
    </slot>

    <FwIcon v-if='iconRight' :icon='iconRight'></FwIcon>
</component>
</template>

<script lang='ts'>
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';

const AVAILABLE_SIZES: Array<string> = ['flex', 'small', 'little', 'little-no-border', 'medium', 'base', 'large', 'large-xl'];
const AVAILABLE_COLORS: Array<string> = ['choose', 'primary', 'secondary', 'blue', 'transparent', 'alert', 'cancel', 'green', 'skip'];
const BORDER_RADIUS_TYPES: Array<string> = ['base-border', 'small-border', 'tiny-border', 'medium-border'];

@Component({
    inheritAttrs: false,
})
export default class FwButton extends Vue {
    @Prop({ type: String, default: null }) readonly iconRight!: string | null;
    @Prop({ type: String, default: null }) readonly iconLeft!: string | null;
    @Prop({ type: String, default: null }) readonly label!: string | null;
    @Prop({ type: String, default: null }) readonly fontSize!: string | null;
    @Prop({ type: String, default: null }) readonly fontColor!: string | null;
    @Prop({ type: String, default: 'white' }) readonly iconColor!: string;
    @Prop({ type: String, default: null }) readonly minWidth!: string;
    @Prop({ type: Boolean, default: false }) readonly paddingHorizontal!: boolean;
    @Prop({ type: Boolean, default: false }) readonly paddingOnlyRight!: boolean;
    @Prop({ type: Boolean, default: false }) readonly loading!: boolean;
    @Prop({ type: Boolean, default: false }) readonly active!: boolean;
    @Prop({ type: Boolean, default: false }) readonly inversed!: boolean;
    @Prop({ type: Boolean, default: false }) readonly disabled!: boolean;
    @Prop({ type: Object, default: null }) readonly styles!: { [key: string]: string };
    @Prop({ type: String, default: 'base', validator: (size: string) => AVAILABLE_SIZES.includes(size) }) readonly size!: string;
    @Prop({ type: String, default: 'primary', validator: (color: string) => AVAILABLE_COLORS.includes(color) })
    readonly color!: string;
    @Prop({ type: String, default: 'base-border', validator: (borderSize: string) => BORDER_RADIUS_TYPES.includes(borderSize) })
    readonly borderRadiusType!: string;

    get component(): string {
        if (this.$attrs.to) {
            return 'router-link';
        } else if (this.$attrs.href) {
            return 'a';
        }
        return 'button';
    }

    get ghost(): boolean {
        return this.loading || this.disabled;
    }

    get sizeClass(): string {
        return `fw-button--${this.size}`;
    }

    get borderRadiusClass(): string {
        return `fw-button--${this.borderRadiusType}`;
    }

    get colorClass(): string {
        return `fw-button--${this.color}`;
    }

    handleClick(event: Event): void {
        if (this.ghost) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
        } else {
            this.$emit('click', event);
        }
    }
};
</script>

<style lang='sass'>
$primary-btn-color: $aqua-marine
$primary-focus-color: $aqua-marine-light
$secondary-btn-color-dark: $main-red
$secondary-btn-color-light: $pale-salmon
$secondary-focus-color: $light-pink
$alert-btn-color: $alert-opacity
$alert-btn-txt-color: $alert-red
$cancel-btn-color: $cancel-opacity
$cancel-btn-txt-color: $cancel-red
$add-btn-color: $main-green-opacity
$add-btn-txt-color: $main-green
$choose-btn-color: white
$choose-btn-txt-color: $main-green
$choose-btn-focus-color: $main-green

.fw-button
    border: 2px solid
    cursor: pointer
    color: white
    font-weight: bold
    box-shadow: $base-box-shadow
    display: flex
    justify-content: center
    align-items: center
    padding: 0 40px
    min-width: 150px
    transition: background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, opacity 0.15
    outline: none
    font-size: 16px
    &--padding__zero
        padding: 0!important
    &--padding__right
        padding: 0 40px 0 0!important

    &.ghost
        background-color: $dark-gray !important
        border-color: $dark-gray !important
        color: #fff !important
        cursor: initial

    &--small
        padding: 6px 20px
        min-width: 0
        font-size: 14px
    &--medium
        height: $input-h
        padding: 0
        min-width: 156px
        font-size: 14px
        font-weight: bold
        line-height: 16px
    &--base
        height: $input-h

    &--little
        height: $input-h-little
        padding: 10px 24px 12px
        border-radius: calc($input-h / 2)
        min-width: 0
        font-size: 14px
    &--little-no-border
        padding: 0 15px
        height: $input-h-little
        border-radius: calc($input-h / 2)
        min-width: 0
    &--flex
        height: 100%
        min-width: 0
        width: 100%
        padding: 0
        flex: 1

    &--large
        height: $input-h-large
        width: 200px
        border-radius: 20px

    &--large-xl
        display: flex
        justify-content: space-between
        padding: 0 30px
        box-shadow: none
        border: none
        height: 60px
        border-radius: 10px

    &--transparent
        height: auto
        min-width: auto
        padding: 0
        background-color: transparent
        border-color: transparent
        box-shadow: none
        color: $dark-blue

        &:hover
            background-color: transparent
            opacity: 0.8
        &.active
            background-color: $aqua-marine
            color: #fff
    &--transparent.ghost
        border: none
        background: transparent!important
        color: rgba(32, 63, 106, 0.2)!important

    &--base-border
        border-radius: 30px

    &--small-border
        border-radius: 5px

    &--tiny-border
        border-radius: 10px

    &--medium-border
        border-radius: 15px

    &--inversed
        box-shadow: none

    &--primary
        background-color: $primary-btn-color
        border-color: $primary-btn-color

        &:hover,
        &:active
            background-color: $primary-focus-color
            border-color: $primary-focus-color

        &.fw-button--inversed
            background-color: white
            color: $primary-btn-color
            border-color: $primary-btn-color

            &:hover,
            &:active
                background-color: lighten($primary-btn-color, 47%)
                border-color: lighten($primary-btn-color, 15%)

    &--blue
        background-color: $alt-blue
        border-color: $alt-blue

        &:hover,
        &:active
            background-color: $alt-blue-hover
            border-color: $alt-blue-hover

        &.fw-button--inversed
            background-color: white
            border-color: $alt-blue-hover
            color: $alt-blue

            &:hover,
            &:active
                background-color: lighten($alt-blue, 65%)
                border-color: $alt-blue-hover

    &--secondary
        background-color: $secondary-btn-color-dark
        border-color: $secondary-btn-color-dark

        &:hover,
        &:active
            background-color: $secondary-btn-color-light
            border-color: $secondary-btn-color-light

        &.fw-button--inversed
            background-color: white
            border-color: $secondary-btn-color-dark
            color: $secondary-btn-color-dark

            &:hover,
            &:active
                background-color: lighten($secondary-btn-color-dark, 46%)
                border-color: lighten($secondary-btn-color-dark, 20%)
    &--choose
        font-size: 18px
        width: 100%
        background-color: $choose-btn-color
        border-color: $choose-btn-color
        color: $alt-blue
        box-shadow: 0 30px 11px -17px rgba(33,63,107,0.05)

        &:hover,
        &:active
            background-color: lighten($choose-btn-focus-color, 47%)
            border-color: lighten($choose-btn-focus-color, 15%)

        &.fw-button--inversed
            background-color: white
            color: $choose-btn-color
            border-color: $choose-btn-color

            &:hover,
            &:active
                background-color: lighten($choose-btn-color, 47%)
                border-color: lighten($choose-btn-color, 15%)
    &--alert
        background-color: $alert-btn-color
        color: $alert-btn-txt-color
        border: none
        width: 156px!important
        &:hover,
        &:active
            background-color: rgba(206, 18, 83, 0.2)
            border-color: rgba(206, 18, 83, 0.2)
    &--cancel
        background-color: $cancel-red
        color: #fff
        font-weight: bold
        border: none
        padding: 10px 20px
        min-width: 0
        font-size: 12px
        &:hover,
        &:active
            background-color: $cancel-opacity
    &--green
        box-shadow: none
        border: none
        background: $add-btn-color
        color: $add-btn-txt-color

    &--skip
        background-color: transparent
        border: 2px solid #a8b4c5
        color: #a8b4c5
</style>
